import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemText,
  MenuItem,
} from '@material-ui/core';
import {safeParseInt} from 'Shared/utilities/parsers';
import {ModalState, showModal} from 'Core/ducks/modal';
import {Empty, NotFound, isLoaded, AsyncPage} from 'Core/models/Async';
import {addFilterParameters} from 'Shared/utilities/addFilterParameters';
import {FileExtension} from 'Shared/components/FileDownloadButton';
import {
  getEnrollmentFileRejectByCaseRequestId,
  getEnrollmentFileRejectsEmail,
  changeEnrollmentFileRejectApplication,
  changeEnrollmentFileRejectsEmailHistory,
  changeEnrollmentFileRejectFileProfile,
  changeEnrollmentFileRejectDetailsFilters,
  changeEnrollmentFileRejectDetail,
  getEnrollmentFileRejectDetailApplications,
  changeSort,
  clearSort,
  clearEnrollmentFileRejectDetails,
} from 'Enrollment/ducks/enrollmentFileProcessingReject';
import {EnrollmentFileRejectApplication} from 'Enrollment/models/EnrollmentFileRejectApplication';
import {EnrollmentFileRejectDetailApplication} from 'Enrollment/models/EnrollmentFileRejectDetailApplication';
import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {ReducerState} from 'rootReducer';
import CardField from 'Shared/components/CardField';
import {renderAsyncView} from 'Shared/components/RenderAsync';
import {toFormattedDateTime, toFormattedDate} from 'Shared/utilities/DateFormatter';
import './EnrollmentFileDetails.sass';
import './EnrollmentFileRejectDetails.sass';
import {Scope} from 'Shared/constants/Scope';
import {selectUserScopes} from 'Users/selectors/userProfileSelectors';
import Column from 'Shared/components/PagedView/Column';
import PagedView from 'Shared/components/PagedView/PagedView';
import {EnrollmentRouteConstants} from 'Enrollment/Routes';
import {useHistory, useParams} from 'react-router-dom';
import {withStringFilterProperties} from 'Shared/components/PagedView/Filter';
import {withValueChangedHook} from 'Shared/utilities/withValueChanged';
import {Page} from 'Core/models/Page';
import * as E from 'fp-ts/lib/Either';
import {pipe} from 'fp-ts/lib/pipeable';
import ModularHost from 'Shared/components/ModularHost/ModularHost';
import ToolbarHeader, {ExportToolbarHeaderIconWithCustomError} from 'Shared/components/Toolbar/ToolbarHeader';
import {EmailRounded, HistoryRounded} from '@material-ui/icons';
import ActionsMenu from 'Shared/components/PagedView/ActionsMenu';
import * as O from 'fp-ts/lib/Option';

const containsRejectsNotIncludedInEmail = (applications?: EnrollmentFileRejectApplication[]) =>
  applications?.some(application => !application.isIncludedInEmail);

const EnrollmentFileRejectDetails = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const {id: id, applicationSource: applicationSource} = useParams();
  
  const {
    enrollmentFileRejectNotes,
    enrollmentFileRejectDetail,
    enrollmentFileRejectApplications,
    enrollmentFilesRejectDetailsFilter,
    enrollmentRejectFileProfileId,
    sort,
  } = useSelector((state: ReducerState) => state.enrollmentFileProcessingReject);

  const scopes = useSelector(selectUserScopes);

  const changeFilter = withValueChangedHook(
    enrollmentFilesRejectDetailsFilter,
    changeEnrollmentFileRejectDetailsFilters,
    dispatch
  );
  const filterProperties = {
    changeFilter,
    filterData: enrollmentFilesRejectDetailsFilter,
  };

  useEffect(() => {
    if (id) {
      dispatch(getEnrollmentFileRejectByCaseRequestId(id!, applicationSource!));
    } else {
      history.push(EnrollmentRouteConstants.ENROLLMENT_FILE_ERRORS);
    }
    return () => {
      dispatch(changeEnrollmentFileRejectDetail(new Empty()));
      dispatch(changeEnrollmentFileRejectFileProfile(new Empty()));
    };
  }, [id]);

  const result = E.fromOption(() => 'The case request identifier could not be parsed.')(safeParseInt(id!));

  const numericCaseRequestId: number | undefined = pipe(
    result,
    E.fold(
      () => undefined,
      value => value
    )
  );

  useEffect(() => {
    return () => {
      dispatch(clearEnrollmentFileRejectDetails());
      dispatch(clearSort());
    };
  }, [clearEnrollmentFileRejectDetails, changeEnrollmentFileRejectDetailsFilters]);

  useEffect(() => {
    dispatch(
      getEnrollmentFileRejectDetailApplications(
        id!,
        applicationSource,
        undefined,
        enrollmentFilesRejectDetailsFilter,
        O.toUndefined(sort)
      )
    );

    return () => {
      dispatch(changeEnrollmentFileRejectApplication(new Empty()));
    };
  }, [dispatch, enrollmentFilesRejectDetailsFilter, sort]);

  const [showNotes, setShowNotes] = useState(false);
  const [allRejectsIncludedInEmail, setAllRejectsIncludedInEmail] = useState(true);

  useEffect(() => {
    if (
      isLoaded(enrollmentFileRejectApplications) &&
      containsRejectsNotIncludedInEmail(enrollmentFileRejectApplications.Data.result)
    ) {
      setAllRejectsIncludedInEmail(false);
    }
  }, [enrollmentFileRejectApplications, sort]);

  const handleApplicationChange = (application: EnrollmentFileRejectApplication) => {
    application.isIncludedInEmail = !application.isIncludedInEmail;
    dispatch(changeEnrollmentFileRejectApplication(enrollmentFileRejectApplications));
    if (!application.isIncludedInEmail && allRejectsIncludedInEmail) {
      setAllRejectsIncludedInEmail(false);
    } else {
      if (
        isLoaded(enrollmentFileRejectApplications) &&
          !containsRejectsNotIncludedInEmail(enrollmentFileRejectApplications.Data.result)
      ) {
        setAllRejectsIncludedInEmail(true);
      }
    }
  };

  const handleEmailBtnClick = (enrollmentFileRejectApplications: AsyncPage<Page<EnrollmentFileRejectApplication>>) => {
    if (isLoaded(enrollmentFileRejectApplications)) {
      dispatch(changeEnrollmentFileRejectsEmailHistory(new Empty()));

      const enrollmentFileRejectDetailApplication: EnrollmentFileRejectDetailApplication = {
        id : id!,
        applicationSource: applicationSource,
        applications: enrollmentFileRejectApplications.Data.result,
      };

      dispatch(
        getEnrollmentFileRejectsEmail({rejectApplications: enrollmentFileRejectDetailApplication, isResend: false})
      );
      dispatch(showModal(ModalState.EnrollmentFileRejectsEmail({isResend: false})));
    }
  };

  const handleSelectAllChanges = (value: boolean) => {
    if (isLoaded(enrollmentFileRejectApplications)) {
      enrollmentFileRejectApplications.Data.result!.map(application => (application.isIncludedInEmail = value));
      dispatch(changeEnrollmentFileRejectApplication(enrollmentFileRejectApplications));
      setAllRejectsIncludedInEmail(value);
    }
  };

  const renderEnrollmentFileErrorsDetail = () => {
    return (
      <>
        <ModularHost>
          <ToolbarHeader
            title="Enrollment Errors Detail"
            selectAllProps={{
              checked: allRejectsIncludedInEmail,
              disabled: !scopes.includes(Scope.SendRejectEmail),
              onChange: e => handleSelectAllChanges(e.target.checked),
            }}
          >
            <ActionsMenu>
              <MenuItem
                id="menu-item-reject-send-email"
                onClick={() => handleEmailBtnClick(enrollmentFileRejectApplications)}
              >
                Email
              </MenuItem>
              <MenuItem
                id="menu-item-reject-email-history"
                onClick={() =>
                  history.push(EnrollmentRouteConstants.ENROLLMENT_FILE_ERRORS_EMAIL_HISTORY(id!, applicationSource!))
                }
              >
                Email History
              </MenuItem>
            </ActionsMenu>
            <ExportToolbarHeaderIconWithCustomError
              title="Export Enrollment File Errors Details"
              downloadedFileName="Enrollment File Errors Details"
              extension={FileExtension.CSV}
              relativeFileLocation={`/enrollmentFiles/processingHistory/rejects/details/applications/csv/${addFilterParameters({
                ...enrollmentFilesRejectDetailsFilter,
               caseRequestId : applicationSource === "ESR" ? id : 0,
               fileId: applicationSource === "GenelcoSpreadsheet" ? id : "",
               applicationSource,
                exportToCsv: true,
              })}`}
              totalRecordCount={isLoaded(enrollmentFileRejectApplications) ? enrollmentFileRejectApplications.Data.totalCount : 0}
            />
          </ToolbarHeader>
          <PagedView
            pagedViewKey="enrollmentFileRejectDetails"
            data={enrollmentFileRejectApplications}
            sort={O.toUndefined(sort)}
            onChangeSort={sort => dispatch(changeSort(sort))}
            loadMore={page =>
              dispatch(
                getEnrollmentFileRejectDetailApplications(
                  id!,
                  applicationSource,
                  page,
                  enrollmentFilesRejectDetailsFilter
                )
              )
            }
          >
            {columnProps => (
              <>
                <Column {...columnProps} title="Email" default>
                  {rejectFile => (
                    <Checkbox
                      disabled={!scopes.includes(Scope.SendRejectEmail)}
                      checked={rejectFile.isIncludedInEmail}
                      key={rejectFile.requestId!}
                      value={rejectFile.isIncludedInEmail}
                      onChange={() => {
                        handleApplicationChange(rejectFile);
                      }}
                    />
                  )}
                </Column>
                <Column
                  {...columnProps}
                  title="Account Number"
                  sortColumnKey="accountNumber"
                  filterProperties={withStringFilterProperties(filterProperties, 'accountNumber')}
                  default
                >
                  {rejectFile => rejectFile.accountNumber}
                </Column>
                <Column
                  {...columnProps}
                  title="First Name"
                  sortColumnKey="firstName"
                  filterProperties={withStringFilterProperties(filterProperties, 'firstName')}
                  default
                >
                  {rejectFile => rejectFile.firstName}
                </Column>
                <Column
                  {...columnProps}
                  title="Last Name"
                  sortColumnKey="lastName"
                  filterProperties={withStringFilterProperties(filterProperties, 'lastName')}
                  default
                >
                  {rejectFile => rejectFile.lastName}
                </Column>
                <Column
                  {...columnProps}
                  title="Product"
                  sortColumnKey="product"
                  filterProperties={withStringFilterProperties(filterProperties, 'product')}
                  default
                >
                  {rejectFile => rejectFile.product}
                </Column>
                <Column
                  {...columnProps}
                  title="Request Type"
                  sortColumnKey="requestType"
                  filterProperties={withStringFilterProperties(filterProperties, 'requestType')}
                  default
                >
                  {rejectFile => rejectFile.requestType}
                </Column>
                <Column {...columnProps} title="Error Date">
                  {rejectFile => toFormattedDate(rejectFile.rejectDate!)}
                </Column>
                <Column
                  {...columnProps}
                  title="Error Reason"
                  sortColumnKey="rejectReason"
                  filterProperties={withStringFilterProperties(filterProperties, 'errorReason')}
                  default
                >
                  {rejectFile => rejectFile.rejectReason! ? rejectFile.rejectReason!.replace(/^(?:ET33|ET 33)/i, '').trim(): ''}
                </Column>
                <Column
                  {...columnProps}
                  title="Action By"
                  sortColumnKey="rejectedBy"
                  filterProperties={withStringFilterProperties(filterProperties, 'rejectedBy')}
                  default
                >
                  {rejectFile =>
                    ((rejectFile.requestId === null || rejectFile.requestId === '') && (rejectFile.fileId === null || rejectFile.fileId === ''))
                      ? 'N/A'
                      : rejectFile.rejectedBy === null || rejectFile.rejectedBy === ''
                      ? 'N/A'
                      : rejectFile.rejectedBy!.replace('AD\\', '')
                  }
                </Column>
                <Column
                  {...columnProps}
                  title="SSN"
                  sortColumnKey="maskedIdentifier"
                  filterProperties={withStringFilterProperties(filterProperties, 'ssn')}
                  default
                >
                  {rejectFile => rejectFile.maskedIdentifier}
                </Column>
              </>
            )}
          </PagedView>
        </ModularHost>
      </>
    );
  };

  const renderHeaderAsync = renderAsyncView(enrollmentFileRejectDetail, rejectFile => (
    <>
      <div className="enrollment-file-details-header__icon-container">
        <i className="icon-container__file-icon"></i>
      </div>
      <div className="enrollment-file-details__file-details">
        <CardField
          className="enrollment-file-details__details-field"
          name="Agency Name"
          value={rejectFile.agencyName}
        />
        <CardField
          className="enrollment-file-details__details-field"
          name="Created Date"
          value={toFormattedDateTime(rejectFile.createdDate!)}
        />
        <CardField
          className="enrollment-file-details__details-field"
          name="Account number"
          value={rejectFile.accountNumber}
        />
        <CardField className="enrollment-file-details__details-field" name="File Name" value={rejectFile.fileName} />
      </div>
    </>
  ));

  const notesDialog = () => (
    <Dialog open={showNotes} onClose={() => setShowNotes(false)} fullWidth maxWidth="sm">
      <DialogTitle>Notes</DialogTitle>
      <DialogContent>
        {enrollmentFileRejectNotes.map((note, index) => (
          <List key={index}>
            <ListItem>
              <ListItemText
                primary={note.noteType}
                secondary={`${toFormattedDateTime(note.modifiedDate)} | By: ${note.modifiedBy}`}
              />
            </ListItem>
            <ListItem>
              <ListItemText primary={note.noteText} />
            </ListItem>
            {index + 1 < enrollmentFileRejectNotes.length && <Divider />}
          </List>
        ))}
      </DialogContent>
    </Dialog>
  );

  return (
    <>
      <div className="enrollment-file-reject-details">
        <div className="enrollment-file-reject-details__action-buttons">
          {enrollmentFileRejectNotes.length > 0 && (
            <Button id="enrollment-view-notes-button" onClick={() => setShowNotes(true)} color="primary">
              View Notes
            </Button>
          )}
          {enrollmentRejectFileProfileId instanceof NotFound ? (
            <Button
              target="fileProfile"
              color="primary"
              href={`${process.env.REACT_APP_ENROLL_TRACK_PORTAL_URL}/fileprofile`}
            >
              New File Profile
            </Button>
          ) : (
            renderAsyncView(enrollmentRejectFileProfileId, fileProfileId => (
              <Button
                target="fileProfile"
                color="primary"
                href={`${process.env.REACT_APP_ENROLL_TRACK_PORTAL_URL}/fileprofile/${fileProfileId}`}
              >
                File Profile
              </Button>
            ))
          )}
        </div>
        <div className="enrollment-file-details-header">{renderHeaderAsync}</div>
        {renderEnrollmentFileErrorsDetail()}
        {notesDialog()}
      </div>
    </>
  );
};

export default EnrollmentFileRejectDetails;
